import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPriceSavingsMessage from "@MasterBluePrint/Overrides/Components/ProductPriceSavingsMessage";
import { getQuote, GetQuoteApiParameter } from "@MasterBluePrint/Services/CustomQuoteService";
import themeColors, { themeFontWeights } from "@MasterBluePrint/Styles/variables";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";


interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    extendedStyles?: ProductPriceStyles;
    hideVat?: boolean;
    hideProductPriceSavingsMessage?: boolean;
    isPartOfCarousel?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    state,
    currentPage: getCurrentPage(state),
    cart: getCurrentCartState(state).value,
    currentPageUrl: getLocation(state).pathname,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    callText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-bottom: 0.75rem;

            span {
                margin-right: .31250625rem;
            }

        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${wrapInContainerStyles}
            p {
                margin-top: 1.3rem;
            }

            &.unitlabeluk {
                span[data-test-selector="productPrice_unitNetPrice"] {
                    color: ${themeColors.colorpriceToggleText} !important;
                    font-family: Lato;
                    font-weight: 400;
                    font-size: 10px;
                    leading-trim: Cap height;
                    line-height: 100%;
                    letter-spacing: 0.55px; 
                }
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    white-space: nowrap;
                    color: ${themeColors.colorpriceToggleText};
                    text-transform: lowercase !important;
                    font-family: Lato;
                    font-weight: 400;
                    font-size: 10px;
                    leading-trim: Cap height;
                    line-height: 100%;
                    letter-spacing: 0.55px;
                }
                .vat-field {
                    color: ${themeColors.colorpriceToggleText};
                    font-family: Lato;
                    font-weight: 400;
                    font-style: italic;
                    font-size: 10px;
                    leading-trim: Cap height;
                    line-height: 100%;
                    letter-spacing: 0.55px; 
                }

                @media screen and (max-width: 1399px) {
                    span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                        margin-left: 0.31250625rem;
                    }                    
                }
            }
        `,
    },
    price: {
        priceText: {
            css: css`
                color: ${themeColors.colorpriceToggleText};
                font-family: Lato;
                font-weight: 400;
                font-size: 10px;
                leading-trim: Cap height;
                line-height: 100%;
                letter-spacing: 0.55px;                

                @media screen and (max-width: 1399px) {
                    color: ${themeColors.colorpriceToggleText};
                    font-family: Lato;
                    font-weight: 400;
                    font-size: 10px;
                    leading-trim: Cap height;
                    line-height: 100%;
                    letter-spacing: 0.55px;  
                }
            `,
        },
        callText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 1.5625rem;
                font-size: 1.68rem !important;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
                color: ${themeColors.colorpriceToggleText};
                text-transform: lowercase !important;
                font-family: Lato;
                font-weight: 400;
                font-size: 10px;
                leading-trim: Cap height;
                line-height: 100%;
                letter-spacing: 0.55px;
                margin-right: .8rem;

                @media screen and (max-width: 1399px) {
                    color: ${themeColors.colorpriceToggleText};
                    font-family: Lato, sans-serif;
                    font-family: Lato;
                    font-weight: 400;
                    font-size: 10px;
                    leading-trim: Cap height;
                    line-height: 100%;
                    letter-spacing: 0.55px;
                }
               
            `,
        },
        vatLabelText: {
            css: css`
            color: ${themeColors.colorpriceToggleText};
            font-family: Lato;
            font-weight: 400;
            font-style: italic;
            font-size: 10px;
            leading-trim: Cap height;
            line-height: 100%;
            letter-spacing: 0.55px;            
            `,
        },
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 0.625rem;
            display: flex;
            flex-wrap: wrap;
            ${wrapInContainerStyles}

            &.secondaryunitlabeluk {
                span[data-test-selector="productPrice_unitNetPrice"] {                                       
                    color: ${themeColors.colorpriceToggleText};
                    font-family: Roboto, sans-serif;
                    font-size: 1.25rem;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    letter-spacing: -0.0238rem;
                    line-height: 1.4375rem;
                }
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: ${themeColors.colorCharcoalGrey};
                    text-transform: lowercase !important;
                    font-size: 1rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                    margin-left: 0.31250625rem;
                }
                .vat-field {
                    color: ${themeColors.colorCharcoalGrey};
                    font-size: .75rem !important;
                    letter-spacing: 0.04rem;
                    line-height: 1.5rem;
                    font-family: Lato, sans-serif;
                }
                @media screen and (max-width: 1399px) {
                    span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                        margin-left: 0.31250625rem;
                    }                    
                }
            }
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 1.5625rem;
                
                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            css: css`
                font-size: .75rem;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: .5rem;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {

        size: "1rem",
        css: css`
        &:first-child {
        padding:.05rem .5rem;
        background: #002554;
        width: auto;
        color:#fff;
        margin: 0 0 1rem;
        max-width: max-content;
    }
    `,
    },
    quoteMessage: {
        text: { weight: "bold" },
    },

};

const SectionWrapper = getStyledWrapper("section");

const ProductPriceToggle = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    hideVat,
    hideProductPriceSavingsMessage,
    state,
    currentPage,
    isPartOfCarousel=false,
    cart,
    currentPageUrl
}: Props) => {
    const currencyCode = state.context.session.currency?.currencyCode;
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));
    const [quote, setQuote] = useState<QuoteModel | null>(null);
    const [isQuoteCart, setIsQuoteCart] = useState(false);
    useEffect(() => {
        if (cart) {
            if (cart.properties?.isquote === "true") {
                setIsQuoteCart(true);
                const parameter: GetQuoteApiParameter = { quoteId: cart.properties?.quoteNumber };
                getQuoteData(parameter).then(quote => {
                    setQuote(quote);
                });
            }
        }
    }, [cart]);

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const showVat = getCookie("ShowVat");
    if (showVat === "true") {
        enableVat = true;
        hideVat = false;
        vatPriceDisplay = "DisplayWithVat"
    } else if (showVat === "false") {
        enableVat = false;
        hideVat = true;
        vatPriceDisplay = "DisplayWithoutVat"
    }

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.description || pricingData.productInfo.unitOfMeasure
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    let pricing;
    if ((currentPageUrl.toLowerCase().includes("cart") || currentPageUrl.toLowerCase().includes("checkout")) && cart != null && cart.properties?.isquote === "true") {
        const quotePrice = quote?.cartLines?.find(line => line.productId === cartLineModel.productId);
        if (quotePrice) {
            pricing = quotePrice.pricing;
        }
    }
    else if ("product" in pricingData) {
        pricing = pricingData.productInfo.pricing;
    } else {
        pricing = pricingData.pricing;
    }


    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}>{siteMessage("Pricing_SignInForPrice")}</Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText}>{siteMessage("RealTimePricing_PriceLoadFailed")}</Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const priceClassName = pricing && pricing.actualPriceDisplay && pricing.actualPriceDisplay.length > 10 ? 'long-length' : '';


    return (
        <SectionWrapper {...styles.wrapper} className={`price-section ${priceClassName}`}>
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
            {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} className={currencyCode === "GBP" ? 'unitlabeluk' : ''}>
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    hideVat={hideVat}
                    isQuoteCart={isQuoteCart}
                />

                {!hideProductPriceSavingsMessage && pricing && (
                    <ProductPriceSavingsMessage
                        pricing={pricing}
                        qtyOrdered={qtyOrdered || 1}
                        showSavingsAmount={false}
                        showSavingsPercent={false}
                        currencySymbol={currencySymbol}
                        {...styles.savingsMessageText}
                    />
                )}

            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} className={currencyCode === "GBP" ? 'secondaryunitlabeluk' : ''}>
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                        hideVat={hideVat}
                        isQuoteCart={isQuoteCart}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}

        </SectionWrapper>
    );
};
export async function getQuoteData(
    parameter: GetQuoteApiParameter,
): Promise<QuoteModel> {
    const quoteProduct = await getQuote(parameter);
    return quoteProduct;
}
interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    hideVat?: boolean;
    isQuoteCart?: boolean;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    hideVat,
    isQuoteCart
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    return (
        <>
            {pricing ? (
                (isQuoteCart ? (
                    (pricing.unitNetPrice || pricing.unitRegularPriceWithVat) > 0 ? (
                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {salePriceLabelPrefix}
                            {enableVat && displayWithVat
                                ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                        </Typography>
                    ) : (
                        <Typography {...styles.callText} data-test-selector="productPrice_unitNetPrice">
                            {siteMessage("Price_Zero_Dollar_Label")}
                        </Typography>
                    )
                ):(pricing.unitListPrice || pricing.unitListPriceWithVat) > 0 ? (
                    <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                        {salePriceLabelPrefix}
                        {!enableVat && !displayWithVat
                            ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                            : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                    </Typography>
                ) : (
                    <Typography {...styles.callText} data-test-selector="productPrice_unitNetPrice">
                        {siteMessage("Price_Zero_Dollar_Label")}
                    </Typography>
                ))
                
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                    {currencySymbol}
                </Typography>
            )}

            {unitOfMeasure && showUnitOfMeasure && (
                <Typography {...styles.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
                    {unitOfMeasure}
                </Typography>
            )}
            {pricing && (
                <Typography {...styles.vatLabelText} className="vat-field">
                    {!displayWithVat ? `(${translate("Incl. VAT")})` : `(${translate("Excl. VAT")})`}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPriceToggle);
